/**
 * ServiceKanban — Kanban board view of services grouped by status.
 * Columns: Running | Stopped | Maintenance | Degraded | Unknown
 */
import React, { useMemo } from 'react';
import ServiceCard from './ServiceCard';
import './ServiceCard.css';

const COLUMNS = [
  { key: 'running',     label: 'Running',      color: '#15803d', bg: '#dcfce7', icon: '🟢' },
  { key: 'stopped',     label: 'Stopped',      color: '#991b1b', bg: '#fee2e2', icon: '🔴' },
  { key: 'maintenance', label: 'Mantenimiento', color: '#854d0e', bg: '#fef9c3', icon: '🟡' },
  { key: 'degraded',    label: 'Degradado',     color: '#9a3412', bg: '#ffedd5', icon: '🟠' },
  { key: 'unknown',     label: 'Desconocido',   color: '#64748b', bg: '#f1f5f9', icon: '⚪' },
];

const KANBAN_CONTAINER = {
  display: 'flex',
  gap: 14,
  overflowX: 'auto',
  paddingBottom: 16,
  minHeight: 300,
};

const COLUMN_STYLE = {
  flex: '1 1 0',
  minWidth: 260,
  maxWidth: 380,
  background: 'var(--bg-surface, #f8fafc)',
  borderRadius: 12,
  border: '1px solid var(--border, #e2e8f0)',
  display: 'flex',
  flexDirection: 'column',
};

const COLUMN_HEADER = {
  padding: '10px 14px',
  borderBottom: '1px solid var(--border, #e2e8f0)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  borderTopLeftRadius: 11,
  borderTopRightRadius: 11,
};

export default function ServiceKanban({ services, onEdit, onDelete }) {
  const columns = useMemo(() => {
    const map = {};
    COLUMNS.forEach(col => { map[col.key] = []; });
    services.forEach(svc => {
      const key = svc.status || 'unknown';
      if (map[key]) map[key].push(svc);
      else map['unknown'].push(svc);
    });
    return map;
  }, [services]);

  const totalCards = services.length;

  if (totalCards === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted, #94a3b8)' }}>
        No hay servicios que mostrar
      </div>
    );
  }

  return (
    <div style={KANBAN_CONTAINER} className="svc-kanban-scroll">
      {COLUMNS.map(col => {
        const cards = columns[col.key] || [];
        return (
          <div key={col.key} style={COLUMN_STYLE}>
            {/* Column header */}
            <div style={{
              ...COLUMN_HEADER,
              background: col.bg,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.85rem' }}>{col.icon}</span>
                <span style={{ fontWeight: 700, fontSize: '0.8rem', color: col.color }}>
                  {col.label}
                </span>
              </div>
              <span style={{
                background: 'rgba(255,255,255,.14)',
                color: col.color,
                fontWeight: 700,
                fontSize: '0.72rem',
                padding: '1px 8px',
                borderRadius: 10,
                minWidth: 22,
                textAlign: 'center',
              }}>
                {cards.length}
              </span>
            </div>

            {/* Cards */}
            <div style={{
              padding: '8px 10px',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              overflowY: 'auto',
              flex: 1,
            }}>
              {cards.map(svc => (
                <ServiceCard
                  key={svc.id}
                  service={svc}
                  onEdit={() => onEdit && onEdit(svc)}
                  onDelete={() => onDelete && onDelete(svc)}
                  compact
                />
              ))}
              {cards.length === 0 && (
                <div style={{
                  textAlign: 'center', padding: '20px 8px',
                  fontSize: '0.72rem', color: 'var(--text-muted, #cbd5e1)',
                }}>
                  Sin servicios
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
